<template>
  <div class="flex flex-col">
    <div v-if="hasProductServices">
      <h6 class="uppercase text-gray-700 font-medium text mb-4">Βασικες Υπηρεσιες</h6>
      <p class="text-sm text-teal-700 mb-16">
        Λίστα υπηρεσιών που περιλαμβάνονται στη τιμή του προϊόντος
      </p>
      <BaseLayoutSpaced>
        <template v-for="productService in productServices">
          <template v-if="upgradesNonOptionalProductService(productService)">
            <ProductServiceUpgrade
              v-if="isProductServiceSelected(productService)"
              :key="productService.id"
              :entity="productService"
              :selected="isProductServiceSelected(productService)"
              @downgrade="downgradeProductService"
            />
          </template>

          <template
            v-else-if="
              !upgradesOptionalProductService(productService) &&
              !isProductServiceOptional(productService)
            "
          >
            <ProductService
              v-if="!isProductServiceUpgraded(productService)"
              :key="productService.id"
              :entity="productService"
              :selected="isProductServiceSelected(productService)"
            >
              <template #default="productServiceProps">
                <ProductServiceUpgrade
                  :entity="productServiceProps.productServiceUpgrade"
                  @upgrade="upgradeProductService"
                />
              </template>
            </ProductService>
          </template>
        </template>
      </BaseLayoutSpaced>
    </div>
    <div v-if="hasOptionalProductServices" class="mt-16">
      <h6 class="uppercase text-gray-700 font-medium text mb-4">Προαιρετικες Υπηρεσιες</h6>
      <p class="text-sm text-teal-700">Λίστα προαιρετικών Υπηρεσιών.</p>
      <p class="text-sm text-orange-700 mb-16">
        Η επιλογή τους διαμορφώνει την τελική τιμή του προϊόντος.
      </p>
      <BaseLayoutSpaced>
        <template v-for="productService in productServices">
          <template v-if="upgradesOptionalProductService(productService)">
            <ProductServiceUpgrade
              v-if="isProductServiceSelected(productService)"
              :key="productService.id"
              :entity="productService"
              :selected="isProductServiceSelected(productService)"
              @downgrade="downgradeProductService"
            />
          </template>

          <template
            v-else-if="
              !upgradesNonOptionalProductService(productService) &&
              isProductServiceOptional(productService)
            "
          >
            <ProductService
              v-if="!isProductServiceUpgraded(productService)"
              :key="productService.id"
              :entity="productService"
              :selected="isProductServiceSelected(productService)"
              @add="addProductService"
              @remove="removeProductService"
            >
              <template #default="productServiceProps">
                <ProductServiceUpgrade
                  :entity="productServiceProps.productServiceUpgrade"
                  @upgrade="upgradeProductService"
                />
              </template>
            </ProductService>
          </template>
        </template>
      </BaseLayoutSpaced>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import ProductService from './basicProductCustomization/ProductService.vue';
import ProductServiceUpgrade from './basicProductCustomization/ProductServiceUpgrade.vue';

export default {
  name: 'BasicProductCustomization',
  components: {
    ProductService,
    ProductServiceUpgrade,
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    entity: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    selectedProductServices: [],
  }),
  computed: {
    ...mapState('entities', {
      productsById: (state) => state.products.byId,
      productServicesById: (state) => state.productServices.byId,
    }),
    productServices() {
      const { productServices = [] } = this.entity || {};
      return productServices
        .map((id) => this.productServicesById[id])
        .filter((productService) => this.productsById[productService.serviceUuid]);
    },
    hasProductServices() {
      return this.productServices.some((productService) => !productService.optional);
    },
    hasOptionalProductServices() {
      return this.productServices.some(
        (productService) => productService.optional && !productService.upgradesProductServiceUuid
      );
    },
  },
  watch: {
    value: {
      deep: true,
      handler(newVal) {
        if (JSON.stringify(newVal) !== JSON.stringify(this.selectedProductServices)) {
          this.setup();
        }
      },
    },
  },
  created() {
    this.setup();
  },
  methods: {
    setup() {
      if (this.value.length) {
        this.selectedProductServices = [...this.value];
      } else {
        this.selectedProductServices = [
          ...this.productServices.filter((productService) => !productService.optional),
        ];
        this.$emit('input', this.selectedProductServices);
      }
    },
    isProductServiceOptional(productService) {
      return productService.optional;
    },
    isProductServiceSelected(productService) {
      const index = this.selectedProductServices.findIndex((x) => x.uuid === productService.uuid);

      return index >= 0;
    },
    isProductServiceUpgrade(productService) {
      const { upgradesProductServiceUuid } = productService;
      return typeof upgradesProductServiceUuid === 'string';
    },
    isProductServiceUpgraded(productService) {
      const { upgrades = [] } = productService;

      return this.selectedProductServices.some(
        (selectedProductService) => upgrades.indexOf(selectedProductService.uuid) >= 0
      );
    },
    upgradesNonOptionalProductService(productServiceUpgrade) {
      const { upgradesProductServiceUuid } = productServiceUpgrade;

      if (typeof upgradesProductServiceUuid !== 'string') {
        return false;
      }

      return this.productServices.some((productService) => {
        if (productService.uuid !== upgradesProductServiceUuid) {
          return false;
        }

        if (productService.optional) {
          return false;
        }

        return true;
      });
    },
    upgradesOptionalProductService(productServiceUpgrade) {
      const { upgradesProductServiceUuid } = productServiceUpgrade;

      if (typeof upgradesProductServiceUuid !== 'string') {
        return false;
      }

      return this.productServices.some((productService) => {
        if (productService.uuid !== upgradesProductServiceUuid) {
          return false;
        }

        if (!productService.optional) {
          return false;
        }

        return true;
      });
    },
    addProductService(productService) {
      this.selectedProductServices.push(productService);
      this.$emit('input', this.selectedProductServices);
    },
    removeProductService(productService) {
      const index = this.selectedProductServices.findIndex((x) => x.uuid === productService.uuid);

      if (index >= 0) {
        this.selectedProductServices.splice(index, 1);
        this.$emit('input', this.selectedProductServices);
      }
    },
    upgradeProductService(productServiceUpgrade) {
      const productServiceUpgradeIndex = this.selectedProductServices.findIndex(
        (productService) => productService.uuid === productServiceUpgrade.uuid
      );

      if (productServiceUpgradeIndex >= 0) {
        return;
      }

      const productServiceIndex = this.selectedProductServices.findIndex(
        (productService) => productService.uuid === productServiceUpgrade.upgradesProductServiceUuid
      );

      if (productServiceIndex >= 0) {
        this.selectedProductServices.splice(productServiceIndex, 1, productServiceUpgrade);
        this.$emit('input', this.selectedProductServices);
      }
    },
    downgradeProductService(productServiceUpgrade) {
      const productService = this.productServices.find(
        (x) => x.uuid === productServiceUpgrade.upgradesProductServiceUuid
      );

      if (!productService) {
        return;
      }

      const productServiceIndex = this.selectedProductServices.findIndex(
        (x) => x.uuid === productServiceUpgrade.upgradesProductServiceUuid
      );

      if (productServiceIndex >= 0) {
        return;
      }

      const productServiceUpgradeIndex = this.selectedProductServices.findIndex(
        (x) => x.uuid === productServiceUpgrade.uuid
      );

      if (productServiceUpgradeIndex >= 0) {
        this.selectedProductServices.splice(productServiceUpgradeIndex, 1, productService);
        this.$emit('input', this.selectedProductServices);
      }
    },
  },
};
</script>
